import React, { useEffect, useRef } from 'react';
import { Compass, PenTool, Code2, Rocket } from 'lucide-react';
import SceneHeader from './SceneHeader';

const steps = [
  { n: '01', title: 'Discover', icon: Compass, text: 'We audit goals, constraints and users — then co-author a product strategy that will survive first contact.', kw: 'Research · Audit · Strategy' },
  { n: '02', title: 'Design',   icon: PenTool, text: 'Interface, UX, data contracts and architecture — all signed off before a single line of production code is written.', kw: 'UX · System · Contracts' },
  { n: '03', title: 'Build',    icon: Code2,   text: 'Weekly production releases with CI/CD, automated tests and quality gates you can see in real time.', kw: 'Engineering · CI/CD · QA' },
  { n: '04', title: 'Launch',   icon: Rocket,  text: 'Launch, observe, iterate. 24/7 monitoring and a post-launch growth partnership that pays for itself.', kw: 'Launch · Ops · Growth' },
];

const Process = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((x) => { if (x.isIntersecting) x.target.classList.add('in-view'); });
    }, { threshold: 0.12 });
    el.querySelectorAll('.reveal, .reveal-stagger').forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, []);

  return (
    <section id="process" ref={ref} className="relative py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SceneHeader
          chapter="04 / 06"
          subtitle="Process"
          title={<>A proven <span className="font-serif neon-text">path</span> to launch.</>}
          description="Four scenes. Zero surprises. The same rhythm we&apos;ve used to ship more than 500 missions."
        />

        <ol className="mt-24 relative reveal-stagger">
          {/* Vertical line */}
          <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />

          {steps.map((s, i) => {
            const Icon = s.icon;
            const isLeft = i % 2 === 0;
            return (
              <li key={s.n} className="relative md:grid md:grid-cols-2 md:gap-20 py-10 md:py-16 first:pt-0">
                {/* Node */}
                <span className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-12 md:top-24 -translate-y-1/2 w-4 h-4 rounded-full bg-black border border-cyan-400/60 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulseGlow" />
                </span>

                {/* Card placement */}
                <div className={`pl-14 md:pl-0 ${isLeft ? 'md:col-start-1 md:pr-16 md:text-right' : 'md:col-start-2 md:pl-16'}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">
                    Scene / {s.n}
                  </div>
                  <h3 className="mt-3 font-light text-4xl md:text-5xl lg:text-6xl chromatic-text leading-[1] tracking-[-0.02em]">
                    {s.title}
                  </h3>
                  <div className={`mt-4 flex items-center gap-3 ${isLeft ? 'md:justify-end' : ''}`}>
                    <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-cyan-300" strokeWidth={1.6} />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-white/45">{s.kw}</span>
                  </div>
                  <p className={`mt-5 text-[15px] text-white/55 leading-relaxed max-w-md ${isLeft ? 'md:ml-auto' : ''}`}>
                    {s.text}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default Process;
